import { useCallback, useRef, useState } from "react"

interface PriceRangeSliderProps {
  min: number
  max: number
  valueMin: number
  valueMax: number
  step?: number
  onChange: (min: number, max: number) => void
}

export default function PriceRangeSlider({ min, max, valueMin, valueMax, step = 1, onChange }: PriceRangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'min' | 'max' | null>(null)

  const getPercent = (value: number) => ((value - min) / (max - min)) * 100

  const getValueFromPosition = useCallback((clientX: number) => {
    const track = trackRef.current
    if (!track) return min
    const rect = track.getBoundingClientRect()
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const raw = min + percent * (max - min)
    const snapped = Math.round(raw / step) * step
    return Math.max(min, Math.min(max, snapped))
  }, [min, max, step])

  const handlePointerDown = (handle: 'min' | 'max') => (e: React.PointerEvent) => {
    e.preventDefault()
    setDragging(handle)
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging) return
    const val = getValueFromPosition(e.clientX)
    if (dragging === 'min') {
      onChange(Math.min(val, valueMax - 1), valueMax)
    } else {
      onChange(valueMin, Math.max(val, valueMin + 1))
    }
  }, [dragging, getValueFromPosition, onChange, valueMin, valueMax])

  const handlePointerUp = useCallback(() => {
    setDragging(null)
  }, [])

  return (
    <div className="hfse-price-slider">
      <div
        className="hfse-price-slider-track"
        ref={trackRef}
      >
        <div
          className="hfse-price-slider-range"
          style={{
            left: `${getPercent(valueMin)}%`,
            width: `${getPercent(valueMax) - getPercent(valueMin)}%`,
          }}
        />
        <div
          className="hfse-price-slider-handle"
          style={{ left: `${getPercent(valueMin)}%` }}
          onPointerDown={handlePointerDown('min')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={valueMin}
        />
        <div
          className="hfse-price-slider-handle"
          style={{ left: `${getPercent(valueMax)}%` }}
          onPointerDown={handlePointerDown('max')}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={valueMax}
        />
      </div>
    </div>
  )
}
